import React, { useContext, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import {
  calculatePlayerAccuracy,
  calculatePlayerAttack,
  calculatePlayerAttackDelaySec,
  calculatePlayerCritChance,
  calculatePlayerDefense,
  calculatePlayerDodgeChance,
  calculatePlayerHPRegenPerSec,
  calculatePlayerMaxHP,
} from "../../backend/exploration/CombatStats";
import { Enemy } from "../../backend/exploration/enemies/Enemy";
import { getCurrentPlayerHP } from "../../backend/exploration/Exploration";
import { PlayerContext } from "../../backend/PlayerContext";
import { formatNumber } from "../../utils/FormattingUtils";
import { GameIcon } from "./icons/GameIcon";
import { Text } from "./Text";

function CombatStatsRow(props: {
  stat: string;
  icon: string;
  value: string;
  hideText?: boolean;
}) {
  return (
    <View style={styles.inlineRow}>
      <View style={styles.inlineRow}>
        <GameIcon icon={props.icon} style={styles.icon} small />
        {!props.hideText && <Text doNotScaleFonts={true}>{props.stat}: </Text>}
      </View>
      <Text doNotScaleFonts={true}>{props.value}</Text>
    </View>
  );
}

const CombatStatsRowMemo = React.memo(CombatStatsRow);

export type CombatStatsDisplay = {
  currentHP: number;
  maxHP: number;
  attack: number;
  attackDelay: number;
  defense: number;
  hpRegen: number;
  accuracy: number;
  dodge: number;
  critChance: number;
};

export function PlayerCombatStatsViewer(props: {
  hideText?: boolean;
  isVisible?: boolean;
}) {
  const playerContext = useContext(PlayerContext);
  const combatStatsDisplayObject = {
    currentHP: getCurrentPlayerHP(playerContext),
    maxHP: calculatePlayerMaxHP(playerContext),
    attack: calculatePlayerAttack(playerContext),
    attackDelay: calculatePlayerAttackDelaySec(playerContext),
    defense: calculatePlayerDefense(playerContext),
    hpRegen: calculatePlayerHPRegenPerSec(playerContext),
    accuracy: calculatePlayerAccuracy(playerContext),
    dodge: calculatePlayerDodgeChance(playerContext),
    critChance: calculatePlayerCritChance(playerContext),
  };
  return useMemo(
    () => (
      <CombatStatsViewer
        {...props}
        showCurrentHP={true}
        combatStats={combatStatsDisplayObject}
      />
    ),
    [props.hideText, props.isVisible, JSON.stringify(combatStatsDisplayObject)],
  );
}

export function EnemyCombatStatsViewer(props: {
  enemy: Enemy;
  hideText?: boolean;
  isVisible?: boolean;
}) {
  const enemy = props.enemy;
  const combatStatsDisplayObject = {
    currentHP: 0,
    maxHP: enemy.getBaseStats().maxHP,
    attack: enemy.getBaseStats().attack,
    attackDelay: enemy.getBaseStats().attackDelay,
    defense: enemy.getBaseStats().defense,
    hpRegen: 0,
    accuracy: 1.0,
    dodge: enemy.getBaseStats().dodgeChance,
    critChance: enemy.getBaseStats().critChance,
  };
  return useMemo(
    () => (
      <CombatStatsViewer
        {...props}
        showCurrentHP={false}
        combatStats={combatStatsDisplayObject}
      />
    ),
    [props.hideText, props.isVisible, JSON.stringify(combatStatsDisplayObject)],
  );
}

export function CombatStatsViewer(props: {
  hideText?: boolean;
  isVisible?: boolean;
  showCurrentHP?: boolean;
  combatStats: CombatStatsDisplay;
}) {
  const combatStats = props.combatStats;
  return useMemo(
    () => (
      <View style={styles.container}>
        <CombatStatsRowMemo
          stat="HP"
          icon="hp"
          hideText={props.hideText}
          value={
            props.showCurrentHP
              ? formatNumber(Math.ceil(combatStats.currentHP)) +
                "/" +
                formatNumber(Math.ceil(combatStats.maxHP))
              : formatNumber(Math.ceil(combatStats.maxHP))
          }
        />
        <CombatStatsRowMemo
          stat="Attack"
          icon="attack"
          hideText={props.hideText}
          value={formatNumber(combatStats.attack)}
        />
        <CombatStatsRowMemo
          stat="Attack Delay"
          icon="attackDelay"
          hideText={props.hideText}
          value={
            formatNumber(combatStats.attackDelay, {
              showDecimals: true,
            }) + " sec"
          }
        />
        <CombatStatsRowMemo
          stat="Defense"
          icon="defense"
          hideText={props.hideText}
          value={formatNumber(combatStats.defense)}
        />
        {combatStats.hpRegen != 0 && (
          <CombatStatsRowMemo
            stat="HP Regen"
            icon="hpRegen"
            hideText={props.hideText}
            value={
              formatNumber(combatStats.hpRegen, {
                showDecimals: true,
              }) + "/sec"
            }
          />
        )}
        {combatStats.accuracy != 1.0 && (
          <CombatStatsRowMemo
            stat="Accuracy"
            icon="accuracy"
            hideText={props.hideText}
            value={
              formatNumber(combatStats.accuracy * 100, {
                showDecimals: false,
              }) + "%"
            }
          />
        )}
        {combatStats.dodge > 0 && (
          <CombatStatsRowMemo
            stat="Dodge Chance"
            icon="dodge"
            hideText={props.hideText}
            value={
              formatNumber(combatStats.dodge * 100, {
                showDecimals: false,
              }) + "%"
            }
          />
        )}
        {combatStats.critChance > 0 && (
          <CombatStatsRowMemo
            stat="Critical Chance"
            icon="critChance"
            hideText={props.hideText}
            value={
              formatNumber(combatStats.critChance * 100, {
                showDecimals: false,
              }) + "%"
            }
          />
        )}
      </View>
    ),
    [
      props.isVisible !== false ? JSON.stringify(combatStats) : false,
      props.hideText,
      props.showCurrentHP,
    ],
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "stretch",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  inlineRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  icon: {
    marginRight: 2,
  },
});
